import { downloadTextFile, fileTimestamp, slugify } from '../../utils/downloadFile';

export type ExportCell = string | number | null;

/** A widget's data flattened to a grid — the one shape both writers below understand. */
export type ExportTable = {
  /** Base of the downloaded file's name, before the timestamp and the extension. */
  name: string;
  columns: string[];
  rows: ExportCell[][];
};

export type ExportDataFormat = 'csv' | 'json';

/** RFC 4180: a field holding one of these is quoted, and a quote inside it is doubled. */
const NEEDS_CSV_QUOTES = /[",\r\n]/;

const csvField = (cell: ExportCell): string => {
  if (cell === null) return '';
  const text = String(cell);
  return NEEDS_CSV_QUOTES.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** Laid out against the header: a short row blanks the rest rather than shifting the columns. */
const csvRow = (cells: ExportCell[], columns: string[]): string =>
  columns.map((_, index) => csvField(cells[index] ?? null)).join(',');

export const toCsvText = ({ columns, rows }: ExportTable): string =>
  [columns.map(csvField).join(','), ...rows.map((row) => csvRow(row, columns))].join('\r\n');

/** One object per row, keyed by column — loadable straight back into a script. */
export const toJsonText = ({ columns, rows }: ExportTable): string =>
  JSON.stringify(
    rows.map((row) => Object.fromEntries(columns.map((column, index) => [column, row[index] ?? null]))),
    null,
    2,
  );

const WRITERS: Record<ExportDataFormat, { write: (table: ExportTable) => string; mediaType: string }> = {
  csv: { write: toCsvText, mediaType: 'text/csv;charset=utf-8' },
  json: { write: toJsonText, mediaType: 'application/json;charset=utf-8' },
};

export const downloadExportTable = (table: ExportTable, format: ExportDataFormat, now = new Date()): void => {
  const { write, mediaType } = WRITERS[format];
  downloadTextFile(write(table), `${slugify(table.name) || 'export'}-${fileTimestamp(now)}.${format}`, mediaType);
};
